/**
 * Every app-wide keyboard shortcut: ⌘ on Apple platforms, Ctrl elsewhere, plus a letter.
 * Keyed by the letter, so two features cannot claim the same shortcut. Register new
 * global shortcuts here rather than with a raw `keydown` listener.
 */
export const MOD_SHORTCUTS = {
  k: "Open Ask AI",
} as const;

export type ModShortcutKey = keyof typeof MOD_SHORTCUTS;

const isApplePlatform = () => /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

/** Whether `event` is the `MOD_SHORTCUTS` entry for `key`, and still unhandled. */
export function matchesModShortcut(event: KeyboardEvent, key: ModShortcutKey): boolean {
  if (
    event.defaultPrevented ||
    event.repeat ||
    event.isComposing ||
    event.altKey ||
    event.shiftKey
  ) {
    return false;
  }
  // On Apple platforms Ctrl+letter is a text-editing command (Ctrl+K deletes to the line end).
  const modifierOnly = isApplePlatform()
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey;
  if (!modifierOnly) {
    return false;
  }
  // A non-Latin layout reports the typed character, so fall back to the physical key.
  const typed = event.key.toLowerCase();
  return /^[a-z]$/.test(typed) ? typed === key : event.code === `Key${key.toUpperCase()}`;
}

/** The shortcut as shown to the user (`⌘K`) and in `aria-keyshortcuts` syntax (`Meta+K`). */
export function modShortcutText(key: ModShortcutKey): { label: string; aria: string } {
  const letter = key.toUpperCase();
  return isApplePlatform()
    ? { label: `⌘${letter}`, aria: `Meta+${letter}` }
    : { label: `Ctrl ${letter}`, aria: `Control+${letter}` };
}
